import {
  ATTR_TRACK_NAME,
  ATTR_TRACK_PROPS,
  track,
  TrackName,
  TrackProps,
} from '@/analytics/track';
import { getLayoverLogger } from '@/logging/LayoverLogger';

// Buttons defined here: https://www.w3.org/TR/pointerevents/#the-button-property
export const BUTTON_PREFIXES = {
  '0': 'click__',
  '2': 'right_click__',
} as const;

export const TRACK_NAME_FORMAT = /^[\w]{1,40}$/;

// Create listeners to send tracking events on elements with [data-track-name]
export function listenForTrackEvents(rootElem: Node): void {
  listenForClicksOnTrackName(rootElem);
}

function getEventTarget(event: Event): EventTarget {
  // @ts-expect-error -- Not all browsers have "composedPath"
  return 'composedPath' in event ? event.composedPath()[0] : event.target;
}

export function listenForClicksOnTrackName(rootElem: Node): void {
  rootElem.addEventListener(
    'pointerdown',
    (event) => {
      try {
        const button = (event as PointerEvent).button;
        // @ts-expect-error -- TS doesn't expect the type conversion here
        const prefix = BUTTON_PREFIXES[button];
        if (!prefix) {
          return;
        }

        const targetEl = getEventTarget(event);
        const trackRecords = getTrackRecordsFromElem(targetEl);
        for (const { name, props } of trackRecords) {
          track(prefix + name, props ?? {});
        }
      } catch (error: any) {
        getLayoverLogger().logError(
          'failed to process click event for counters',
          error,
        );
      }
    },
    /* useCapture */ true,
  );
}

export type TrackRecord = {
  name: TrackName;
  props?: TrackProps;
};

// Collect [data-track-name] attributes up the DOM tree
export function getTrackRecordsFromElem(
  target: EventTarget | null,
): TrackRecord[] {
  const trackRecords: TrackRecord[] = [];

  let node = target;
  while (
    node instanceof window.HTMLElement ||
    node instanceof window.SVGElement
  ) {
    const elem = node.closest('[' + ATTR_TRACK_NAME + ']');
    if (!elem) {
      break;
    }

    const name = elem.getAttribute(ATTR_TRACK_NAME);
    if (name && TRACK_NAME_FORMAT.test(name)) {
      const record: TrackRecord = { name };
      const json = elem.getAttribute(ATTR_TRACK_PROPS);
      if (json) {
        try {
          record.props = JSON.parse(json);
        } catch (error: any) {
          getLayoverLogger().logError(
            `failed to parse ${ATTR_TRACK_PROPS} [name=${
              name ? `"${name}"` : 'unknown'
            }]`,
            error,
          );
        }
      }
      trackRecords.push(record);
    } else {
      console.warn(
        `element with [${ATTR_TRACK_NAME}] was found, but the value was incorrect [name=${
          name ? `"${name}"` : 'unknown'
        }"]`,
      );
    }
    node = elem.parentNode;
  }

  return trackRecords;
}
